"use client";

import { CustomButton } from "@/components/ui/custom-button";
import Image from "next/image";
import { Link } from "@/i18n/navigation";

interface BenefitCard {
	title?: string;
	description?: string;
	image: string;
	imageAlt?: string;
	bgColor: string;
	textColor?: string;
	onlyImage?: boolean;
}

interface BenefitsSectionProps {
	heading: string;
	subheading1: string;
	subheading2: string;
	cards: BenefitCard[];
	buttonText: string;
	buttonHref?: string;
	showButton: boolean;
}

export function BenefitsSection({
	heading,
	subheading1,
	subheading2,
	cards,
	buttonText,
	buttonHref,
	showButton
}: BenefitsSectionProps) {
	return (
		<div className="my-2 lg:my-8 flex w-full flex-col">
			{/* Text content with constrained width */}
			<div className="container mx-auto px-4 sm:px-6 lg:px-8 pb-6 md:pb-8 lg:pb-20">
				<div className="flex flex-col lg:w-2/3">
					<div className="flex flex-col justify-start pb-4 md:pb-8 text-left leading-[40px] sm:leading-[50px] lg:leading-[70px] md:leading-[60px]">
						<h2>
							{heading}
						</h2>
					</div>
					<div className="max-w-5xl text-left mb-4 md:mb-6">
						<h4>
							{subheading1}
						</h4>
					</div>
					<div className="max-w-4xl text-left">
						<p className="text-sm sm:text-base">
							{subheading2}
						</p>
					</div>
				</div>
			</div>

			{/* Full width cards section */}
			<div className="sm:mt-6 md:mt-8 w-full">
				<div className="container mx-auto px-4 sm:px-6 lg:px-8">
					<div className="grid grid-cols-1 gap-8 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
						{cards.map((card, index) => (
							<div key={index} className={`${card.bgColor} rounded-3xl ${card.onlyImage ? 'p-0 overflow-hidden' : 'p-6 sm:p-8 md:p-12 lg:p-12'} ${card.textColor || 'text-white'} h-96 sm:h-112 relative ${index === 2 ? 'md:col-span-2 lg:col-span-1 flex flex-col md:flex-row-reverse md:gap-12 lg:gap-0 lg:flex-col' : 'flex flex-col'}`}>
								{/* Text content */}
								{(card.title || card.description) && !card.onlyImage && (
									<div className={`${index === 2 ? 'shrink-0 md:flex-1 md:flex md:flex-col md:justify-center lg:shrink-0 lg:block' : 'shrink-0'}`}>
										{card.title && <h3 className={`relative z-10 capitalize ${card.textColor || 'text-white'} mb-3 sm:mb-4 md:text-3xl!`}>{card.title}</h3>}
										{card.description && (
											<p className={`${card.textColor || 'text-white'} relative z-10 md:text-[1rem]! leading-tight!`}>
												{card.description}
											</p>
										)}
									</div>
								)}

								{/* Image - always at bottom */}
								<div className={`relative z-30 ${index === 2 ? 'flex-1 md:flex-none md:w-1/2 lg:flex-1 lg:w-full' : 'flex-1'} flex ${card.onlyImage
										? 'h-full w-full items-center justify-center'
										: !card.title && !card.description
											? 'items-center justify-center h-full'
											: index === 2
												? 'items-end md:items-center md:justify-center lg:items-end'
												: 'items-end'
									}`}>
									<Image
										src={card.image}
										alt={card.imageAlt || ''}
										width={1400}
										height={700}
										className={`${card.onlyImage
												? 'w-full h-full object-contain'
												: !card.title && !card.description
													? 'w-full h-auto object-contain'
													: index === 2
														? 'w-3/4 sm:w-8/12 md:w-full h-auto object-contain md:mt-5 lg:mt-10 xl:mt-20'
														: 'w-3/4 sm:w-8/12 md:w-full h-auto object-contain md:mt-5 lg:mt-10 xl:mt-20'
											}`}
									/>
								</div>
							</div>
						))}
					</div>
				</div>
			</div>

			{/* Learn more button - aligned to left */}
			{showButton && (
				<div className="container mx-auto px-4 sm:px-6 lg:px-8 lg:mb-8">
					<div className="flex flex-col lg:w-2/3">
						<div className="mt-6 sm:mt-10 lg:mt-32">
							{buttonHref ? (
								<Link href={buttonHref} className="inline-flex">
									<CustomButton variant="outline" size="lg">
										{buttonText}
									</CustomButton>
								</Link>
							) : (
								<CustomButton variant="primary" size="lg">
									{buttonText}
								</CustomButton>
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
}